import React, { Component } from "react";
import { connect } from "react-redux";
import { getTranslate } from "react-localize-redux";
import { withStyles } from "material-ui/styles/index";
import {ComboBox, Item, Heading, Checkbox, TextField, Button, ActionButton, TooltipTrigger, Tooltip} from "@adobe/react-spectrum";
import { Info } from "lucide-react";
import Grid from "material-ui/Grid";
import history from "../../../history";
import styles from "./styles";
import {
  getSmartFlagProfiles,
  getSmartFlagProfileDetail,
  updateSmartFlagProfile,
  deleteSmartFlagProfile,
  resetSmartFlagProfileDetail, testsMetadataStandards, fiberTestsMetadataStandards
} from "../../../redux/actions";
import DeleteConfirmModal
  from "../../../components/smart-flags/delete-confirm-modal";
import {
  metersToFeetDecimal,
  feetToMetersDecimal,
  getFormattedNumber
} from "../../../common/utils";
import _ from "lodash";

const FLAG_LABEL_KEYS = {
  TestStandard: "test_cable_standard",
  TestLimit: "test_cable_type",
  Length: "result_length_f",
  NextMargin: "report_next",
  ReturnLossMargin: "report_rtnloss",
  InsertionLossMargin: "smart_flag_label_insertion_loss_margin",
  ACRFMargin: "smart_flag_label_acr_f_margin",
  TimeStamp: "timestamp_header",
  AdapterTypeVTestStandard: "adapter_type",
  AdapterType: "adapter_type",
  CalibrationDate: "calibration_date",
  MMFiberType: "smart_flag_label_multimode_fiber_type",
  SMFiberType: "smart_flag_label_singlemode_fiber_type",
  ReferenceType: "smart_flag_label_test_cord_reference",
  AdapterCount: "smart_flag_label_adapter_count",
  SpliceCount: "smart_flag_label_splice_count",
  WavelengthCount: "smart_flag_label_wavelength_count",
};

const WAVELENGTH_MARGIN_FLAGS = {
  MM850InsertionLossMargin: "MM 850",
  MM1300InsertionLossMargin: "MM 1300",
  SM1310InsertionLossMargin: "SM 1310",
  SM1550InsertionLossMargin: "SM 1550",
};

class SmartFlagsProfileEditPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: "",
      flags: [],
      showConfirmDelete: false,
      isSaving: false,
      customerUnit: "",
      isLoaded: false,
      unitsConverted: false,
    };
  }

  componentDidMount() {
    const profileId = parseInt(this.props.match.params.id, 10);
    const { getStandards, getSmartFlagProfileDetail } = this.props;

    getSmartFlagProfileDetail(profileId);
    getStandards();
    this.props.getFiberStandards();
  }

  componentDidUpdate(prevProps) {
    const { profileDetail } = this.props;

    if (profileDetail && prevProps.profileDetail !== profileDetail) {
      this.setState({
        name: profileDetail.name || "",
        customerUnit: profileDetail.measurementUnit || "Meters",
        flags: this.normalizeFlags(profileDetail.flags),
      }, () => this.checkLoaded());
    }
  }

  getReturnPath() {
    return this.props.location?.state?.returnTo || "/smartflags";
  }

  navigateBackToOrigin() {
    history.push({
      pathname: this.getReturnPath(),
      state: {
        refreshSmartFlags: true
      }
    });
  }

  getDefaultSelectValue(flag) {
    if (!flag) return null;

    if (flag.categoryFlag === 'MMFiberType') return 'OM4';
    if (flag.categoryFlag === 'SMFiberType') return 'OS2';
    if (flag.categoryFlag === 'ReferenceType') return '1 Jumper';

    return null;
  }

  normalizeFlags(flags) {
    if (!flags) return [];

    return flags.map(flag => {
      const defaultSelectValue = this.getDefaultSelectValue(flag);

      return {
        ...flag,
        displayUnit: flag.unit,
        textValue: flag.isActive && !flag.textValue && defaultSelectValue
          ? defaultSelectValue
          : flag.textValue,
      };
    });
  }

  checkLoaded() {
    const { customerUnit, unitsConverted } = this.state;

    if (customerUnit) {
      this.setState({ isLoaded: true });

      if (!unitsConverted) {
        if (customerUnit === "Feet") {
          this.convertFlagsMetersToFeet();
          this.setState({ unitsConverted: true });
        }
      }
    }
  }

  convertToFeet(value) {
    return value !== null && value !== undefined ? getFormattedNumber(metersToFeetDecimal(value)) : value;
  }

  convertFlagsMetersToFeet() {
    const { flags } = this.state;

    const convertedFlags = flags.map(flag => {
      if (flag.unit === "m") {
        return {
          ...flag,
          minValue: this.convertToFeet(flag.minValue),
          maxValue: this.convertToFeet(flag.maxValue),
          defaultValue: this.convertToFeet(flag.defaultValue),
          displayUnit: "ft",
        };
      }
      return flag;
    });

    this.setState({ flags: convertedFlags });
  }

  convertToMeters(value) {
    return value !== null && value !== undefined ? getFormattedNumber(feetToMetersDecimal(value)) : value;
  }

  convertFlagsBackToMeters() {
    const { flags, customerUnit } = this.state;

    if (customerUnit !== "Feet") {
      return flags;
    }

    return flags.map(f => {
      if (f.unit === "m") {
        return {
          ...f,
          minValue: this.convertToMeters(f.minValue),
          maxValue: this.convertToMeters(f.maxValue),
          defaultValue: this.convertToMeters(f.defaultValue),
        };
      }
      return f;
    });
  }

  handleNameChange(value) {
    this.setState({ name: value });
  }

  handleFlagChange(flag, fieldName, value) {
    console.log('handleFlagChange', flag, fieldName, value);
    const { flags } = this.state;
    const newFlags = flags.map(f =>
      (f.category === flag.category && f.categoryFlag === flag.categoryFlag)
        ? { ...f, [fieldName]: value }
        : f
    );
    this.setState({ flags: newFlags });
  }

  handleFlagActiveChange(flag, selected) {
    const defaultSelectValue = selected && !flag.textValue
      ? this.getDefaultSelectValue(flag)
      : null;

    const { flags } = this.state;
    const newFlags = flags.map(f => {
      if (f.category !== flag.category || f.categoryFlag !== flag.categoryFlag) {
        return f;
      }

      return {
        ...f,
        isActive: selected,
        textValue: defaultSelectValue || f.textValue,
      };
    });

    this.setState({ flags: newFlags });
  }

  isWholeNumberFlag(flag) {
    return flag.category === 'Fiber' && ['AdapterCount', 'SpliceCount'].includes(flag.categoryFlag);
  }

  handleNumberValueChange(flag, value) {
    const normalizedValue = value === null || value === undefined ? '' : `${value}`;

    if (this.isWholeNumberFlag(flag) && normalizedValue !== '' && !/^\d+$/.test(normalizedValue)) {
      return;
    }

    this.handleFlagChange(flag, 'defaultValue', normalizedValue);
  }

  handleSave() {
    const { name, isSaving } = this.state;
    const profileId = parseInt(this.props.match.params.id, 10);
    const { profile } = this.props;

    if (isSaving || !name) return;

    this.setState({ isSaving: true });

    const flagsToSave = this.convertFlagsBackToMeters();
    const data = {
      name,
      flags: flagsToSave.map(f => ({
        category: f.category,
        categoryFlag: f.categoryFlag,
        isActive: f.isActive,
        minValue: f.minValue,
        maxValue: f.maxValue,
        defaultValue: f.defaultValue,
        textValue: f.textValue,
      })),
    };

    this.props.updateSmartFlagProfile(profileId, data).then(() => {
      this.setState({ isSaving: false });
      if (profile && profile.customerid) {
        this.props.getSmartFlagProfiles(profile.customerid);
      }
      this.navigateBackToOrigin();
    }).catch(() => {
      this.setState({ isSaving: false });
    });
  }

  handleDelete() {
    const profileId = parseInt(this.props.match.params.id, 10);
    const { profile, deleteSmartFlagProfile, getSmartFlagProfiles } = this.props;

    deleteSmartFlagProfile(profileId).then(() => {
      if (profile && profile.customerid) {
        getSmartFlagProfiles(profile.customerid);
      }
      this.navigateBackToOrigin();
    });
  }

  getValidationErrors() {
    const { flags } = this.state;
    const { translate } = this.props;
    const errors = {};

    flags.forEach(flag => {
      if (!flag.isActive) return;
      const key = `${flag.category}-${flag.categoryFlag}`;
      const isNumberType = flag.valueType === 'number';
      const isSelectType = flag.categoryFlag === 'MMFiberType' ||
        flag.categoryFlag === 'SMFiberType' ||
        flag.categoryFlag === 'ReferenceType';

      if (isNumberType) {
        const val = flag.defaultValue;
        if (val === null || val === undefined || val === '') {
          errors[key] = translate('error_enter_value');
        } else {
          const num = Number(val);
          const min = flag.minValue !== null && flag.minValue !== undefined ? Number(flag.minValue) : null;
          const max = flag.maxValue !== null && flag.maxValue !== undefined ? Number(flag.maxValue) : null;

          if (!Number.isFinite(num)) {
            errors[key] = translate('error_enter_value');
          } else if (this.isWholeNumberFlag(flag) && !Number.isInteger(num)) {
            errors[key] = translate('smart_flag_enter_value_between', { min, max });
          } else if ((min !== null && num < min) || (max !== null && num > max)) {
            errors[key] = translate('smart_flag_enter_value_between', { min, max });
          }
        }
      } else if (isSelectType) {
        if (!flag.textValue) {
          errors[key] = translate('error_select_value');
        }
      }
    });

    return errors;
  }

  renderFlagError(flag) {
    const { classes } = this.props;
    const error = this.getValidationErrors()[`${flag.category}-${flag.categoryFlag}`];
    if (!error) return null;
    return <div className={classes.errorText}>{error}</div>;
  }

  renderHelpIcon(helpText) {
    if (!helpText) return null;
    return (
      <TooltipTrigger>
        <ActionButton isQuiet UNSAFE_style={{ padding: 0, minWidth: 'auto', marginLeft: 6, '--spectrum-actionbutton-quiet-background-color-hover': 'transparent', '--spectrum-actionbutton-quiet-background-color-down': 'transparent' }}>
          <Info size={18} />
        </ActionButton>
        <Tooltip>{helpText}</Tooltip>
      </TooltipTrigger>
    );
  }

  getFlagHelpText(flag) {
    const { translate } = this.props;
    const helpTextKeys = {
      "Copper:TestStandard": "smart_flag_test_standard_help",
      "Copper:TestLimit": "smart_flag_test_standard_help",
      "Copper:Length": "smart_flag_suspicious_deviation_help",
      "Copper:NextMargin": "smart_flag_suspicious_deviation_help",
      "Copper:ReturnLossMargin": "smart_flag_suspicious_deviation_help",
      "Copper:InsertionLossMargin": "smart_flag_suspicious_deviation_help",
      "Copper:TimeStamp": "smart_flag_time_stamp_help",
      "Copper:AdapterTypeVTestStandard": "smart_flag_copper_adapter_type_help",
      "Copper:AdapterType": "smart_flag_copper_adapter_type_help",
      "Copper:CalibrationDate": "smart_flag_copper_calibration_help",
      "CopperMinPerf:NextMargin": "smart_flag_minimum_margin_help",
      "CopperMinPerf:ReturnLossMargin": "smart_flag_minimum_margin_help",
      "CopperMinPerf:InsertionLossMargin": "smart_flag_minimum_margin_help",
      "CopperMinPerf:ACRFMargin": "smart_flag_minimum_margin_help",
      "Fiber:TestStandard": "smart_flag_test_standard_help",
      "Fiber:TestLimit": "smart_flag_test_standard_help",
      "Fiber:MMFiberType": "smart_flag_fiber_type_help",
      "Fiber:SMFiberType": "smart_flag_fiber_type_help",
      "Fiber:ReferenceType": "smart_flag_test_cord_reference_help",
      "Fiber:Length": "smart_flag_suspicious_deviation_help",
      "Fiber:InsertionLossMargin": "smart_flag_suspicious_deviation_help",
      "Fiber:AdapterCount": "smart_flag_adapter_count_help",
      "Fiber:SpliceCount": "smart_flag_splice_count_help",
      "Fiber:WavelengthCount": "smart_flag_wavelength_count_help",
      "Fiber:CalibrationDate": "smart_flag_fiber_calibration_help",
      "Fiber:TimeStamp": "smart_flag_time_stamp_help",
      "FiberMinPerf:MM850InsertionLossMargin": "smart_flag_minimum_margin_help",
      "FiberMinPerf:MM1300InsertionLossMargin": "smart_flag_minimum_margin_help",
      "FiberMinPerf:SM1310InsertionLossMargin": "smart_flag_minimum_margin_help",
      "FiberMinPerf:SM1550InsertionLossMargin": "smart_flag_minimum_margin_help",
    };
    const helpTextKey = helpTextKeys[`${flag.category}:${flag.categoryFlag}`];

    return helpTextKey ? translate(helpTextKey) : flag.helpText;
  }

  getFlagLabel(flag) {
    const { translate } = this.props;
    const wavelength = WAVELENGTH_MARGIN_FLAGS[flag.categoryFlag];

    if (wavelength) {
      return `${wavelength} ${translate("smart_flag_label_insertion_loss_margin")}`;
    }

    const translationKey = FLAG_LABEL_KEYS[flag.categoryFlag];
    return translationKey ? translate(translationKey) : flag.name;
  }

  renderNumberRange(flag) {
    const { classes } = this.props;
    const isWholeNumber = this.isWholeNumberFlag(flag);

    return <>
      <Grid item xs={7}>
        <div className={classes.defaultValue}>
          <TextField
            type="number"
            inputMode={isWholeNumber ? 'numeric' : 'decimal'}
            step={isWholeNumber ? 1 : 'any'}
            value={flag.defaultValue ?? ""}
            width="size-1000"
            aria-label="Default value"
            onChange={(v) => this.handleNumberValueChange(flag, v)}
          />
          <span className={classes.unit}>{flag.displayUnit}</span>
          {this.renderHelpIcon(this.getFlagHelpText(flag))}
        </div>
        {this.renderFlagError(flag)}
      </Grid>
    </>;
  }

  renderMMFiberType(flag) {
    let fiberTypes = ['OM1', 'OM2', 'OM3', 'OM4', 'OM5'];
    const selectedKey = flag.textValue || this.getDefaultSelectValue(flag);

    return <Grid item xs={7}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ComboBox selectedKey={selectedKey}
                    onSelectionChange={(v) => this.handleFlagChange(flag, "textValue", v)}
                    width="size-2000"
                    aria-label="MM Fiber Type">
            {fiberTypes.map((fiberType) => {
              return (
                <Item key={fiberType}>{fiberType}</Item>
              );
            })}
          </ComboBox>
          {this.renderHelpIcon(this.getFlagHelpText(flag))}
        </div>
        {this.renderFlagError(flag)}
      </div>
    </Grid>;
  }

  renderSMFiberType(flag) {
    let fiberTypes = ['OS1', 'OS2'];
    const selectedKey = flag.textValue || this.getDefaultSelectValue(flag);

    return <Grid item xs={7}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ComboBox selectedKey={selectedKey}
                    onSelectionChange={(v) => this.handleFlagChange(flag, "textValue", v)}
                    width="size-2000"
                    aria-label="SM Fiber Type">
            {fiberTypes.map((fiberType) => {
              return (
                <Item key={fiberType}>{fiberType}</Item>
              );
            })}
          </ComboBox>
          {this.renderHelpIcon(this.getFlagHelpText(flag))}
        </div>
        {this.renderFlagError(flag)}
      </div>
    </Grid>;
  }

  renderReferenceType(flag) {
    const { classes } = this.props;
    let jumpers = ['1 Jumper', '2 Jumper', '3 Jumper'];
    const selectedKey = flag.textValue || this.getDefaultSelectValue(flag);

    return <Grid item xs={7}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ComboBox selectedKey={selectedKey}
                    onSelectionChange={(v) => this.handleFlagChange(flag, "textValue", v)}
                    width="size-2000"
                    aria-label="Test Cord Reference">
            {jumpers.map((jumper) => {
              return (
                <Item key={jumper}>{jumper}</Item>
              );
            })}
          </ComboBox>
          {this.renderHelpIcon(flag.helpText)}
        </div>
        {this.renderFlagError(flag)}
      </div>
    </Grid>;
  }

  smartFlagsGrid(flags, isCopper) {
    const { classes } = this.props;

    if (!flags || flags.length === 0) return null;
    const sortedFlags = _.sortBy(flags, ['categoryOrder']);

    return sortedFlags.map(flag => {
      const isNumberType = flag.valueType === 'number';
      const isMMFiberType = !isCopper && flag.categoryFlag === 'MMFiberType';
      const isSMFiberType = !isCopper && flag.categoryFlag === 'SMFiberType';
      const isReferenceType = !isCopper && flag.categoryFlag === 'ReferenceType';
      const isOther = !isNumberType &&
        !isMMFiberType && !isSMFiberType && !isReferenceType;

      return <React.Fragment key={`${flag.category}-${flag.categoryFlag}`}>
        <Grid item xs={5} className={classes.firstColumn}>
          <Checkbox isSelected={flag.isActive || false}
                    onChange={(s) => this.handleFlagActiveChange(flag, s)}>{this.getFlagLabel(flag)}</Checkbox>
          {!isNumberType && this.renderHelpIcon(this.getFlagHelpText(flag))}
        </Grid>
        {isNumberType && this.renderNumberRange(flag)}
        {isMMFiberType && this.renderMMFiberType(flag)}
        {isSMFiberType && this.renderSMFiberType(flag)}
        {isReferenceType && this.renderReferenceType(flag)}
        {isOther && <Grid item xs={7}></Grid>}
      </React.Fragment>;
    });
  }

  render() {
    const { classes, profileDetail, profileDetailError, profileDetailErrorMessage, translate, impersonating, softwareUpdate } = this.props;
    const { name, flags, isSaving, showConfirmDelete, isLoaded, customerUnit } = this.state;
    const validationErrors = this.getValidationErrors();
    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const topOffset = 96 + (impersonating && softwareUpdate ? 120 : impersonating || softwareUpdate ? 60 : 0);

    if (profileDetailError && !profileDetail) {
      return (
        <div className={classes.root} style={{ paddingTop: topOffset + 'px' }}>
          <Heading level={1} marginTop="size-0">{translate('smart_flag_edit_profile')}</Heading>
          <div className={classes.loadError}>
            <p>{profileDetailErrorMessage || translate('smart_flag_profile_load_error')}</p>
            <Button variant="secondary" onPress={() => this.navigateBackToOrigin()}>
              {translate('smart_flag_back_to_profiles')}
            </Button>
          </div>
        </div>
      );
    }

    if (!isLoaded) return null;

    const copperFlags = flags.filter(sf => sf.category === "Copper");
    const copperMinPerfFlags = flags.filter(sf => sf.category === "CopperMinPerf");
    const fiberFlags = flags.filter(sf => sf.category === "Fiber");
    const fiberMinPerfFlags = flags.filter(sf => sf.category === "FiberMinPerf");

    return (
      <div className={classes.root} style={{ paddingTop: topOffset + 'px' }}>
        <Heading level={1} marginTop="size-0">{translate('smart_flag_edit_profile')}</Heading>

        <p>{translate('smart_flag_profile_editor_intro')}</p>
        <p>{translate("smart_flag_profiles_suspicious_check_desc")}</p>
        <p>{translate('smart_flag_minimum_performance_description')}</p>

        <Grid container spacing={8}>
          <Grid item xs={6}>
            <div className={classes.nameField}>
              <TextField
                label={translate('profile_name')}
                value={name}
                width="size-5000"
                onChange={(v) => this.handleNameChange(v)}
                isRequired
              />
            </div>
          </Grid>
        </Grid>

        {flags.length > 0 && (
          <Grid container spacing={8}>
            <Grid item xs={12}>
              <div className={classes.sectionTitle}>
                <span className={classes.copperTitle}>{translate('smart_flag_copper_suspicious_check')}</span>
                {this.renderHelpIcon(translate("smart_flag_suspicious_test_check_help"))}
              </div>
            </Grid>
            {this.smartFlagsGrid(copperFlags, true)}
            <Grid item xs={12}>
              <div className={classes.sectionTitle}>
                <span className={classes.minPerfTitle}>{translate('smart_flag_copper_minimum_performance_check')}</span>
                {this.renderHelpIcon(translate("smart_flag_minimum_performance_check_help"))}
              </div>
            </Grid>
            {this.smartFlagsGrid(copperMinPerfFlags, true)}
            <Grid item xs={12}>
              <div className={classes.sectionTitle}>
                <span className={classes.fiberTitle}>{translate('smart_flag_fiber_suspicious_check')}</span>
                {this.renderHelpIcon(translate("smart_flag_suspicious_test_check_help"))}
              </div>
            </Grid>
            {this.smartFlagsGrid(fiberFlags, false)}
            <Grid item xs={12}>
              <div className={classes.sectionTitle}>
                <span className={classes.minPerfTitle}>{translate('smart_flag_fiber_minimum_performance_check')}</span>
                {this.renderHelpIcon(translate("smart_flag_minimum_performance_check_help"))}
              </div>
            </Grid>
            {this.smartFlagsGrid(fiberMinPerfFlags, false)}
          </Grid>
        )}

        <div className={classes.buttons}>
          <Button
            variant="secondary"
            onPress={() => this.navigateBackToOrigin()}
          >
            {translate('general_cancel')}
          </Button>
          <Button
            variant="primary"
            onPress={() => this.setState({ showConfirmDelete: true })}
          >
            {translate('general_delete')}
          </Button>
          <Button
            variant="accent"
            style="fill"
            onPress={() => this.handleSave()}
            isDisabled={!name || isSaving || hasValidationErrors}
          >
            {isSaving ? translate('smart_flag_saving') : translate('save')}
          </Button>
        </div>

        <DeleteConfirmModal
          isOpen={showConfirmDelete}
          onCancel={() => this.setState({ showConfirmDelete: false })}
          onConfirm={() => this.handleDelete()}
          translate={translate}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.profileReducer.profile,
  profileDetail: state.smartFlagProfileReducer.profileDetail,
  profileDetailError: state.smartFlagProfileReducer.error,
  profileDetailErrorMessage: state.smartFlagProfileReducer.errorMessage,
  impersonating: state.userReducer?.impersonating || false,
  softwareUpdate: state.userReducer?.softwareUpdate || false,
  standards: state.testsReducer.standards,
  fiberStandards: state.testsReducer.fiberStandards,
  translate: getTranslate(state.localeReducer),
  ...state
});

const mapDispatchToProps = (dispatch) => ({
  getSmartFlagProfiles: (organisationId) => {
    dispatch(getSmartFlagProfiles(organisationId));
  },
  getSmartFlagProfileDetail: (id) => {
    dispatch(getSmartFlagProfileDetail(id));
  },
  updateSmartFlagProfile: (id, data) => {
    return dispatch(updateSmartFlagProfile(id, data));
  },
  deleteSmartFlagProfile: (id) => {
    return dispatch(deleteSmartFlagProfile(id));
  },
  resetSmartFlagProfileDetail: () => {
    dispatch(resetSmartFlagProfileDetail());
  },
  getStandards: () => {
    return dispatch(testsMetadataStandards());
  },
  getFiberStandards: () => {
    return dispatch(fiberTestsMetadataStandards());
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(SmartFlagsProfileEditPage));
